// Itens de parede (vão na parede norte, y = 0 da planta). Não bloqueiam a caminhada.
// Altura máxima de 18 px: cabe na faixa da parede (WALL_H = 24 px, com rodapé e topo).
// 'relogio' e 'janela' são o GABARITO da Onda 0; o resto é o lote da H5.

import { Pix } from '../pix.js';
import type { FurnitureDef } from '../types.js';

function clock(): Pix {
  const pix = new Pix(12, 12);
  pix.disc(5.5, 5.5, 5, 'f').disc(5.5, 5.5, 4, 'w');
  pix.vline(5, 2, 4, 'k').hline(6, 6, 3, 'k').px(5, 6, 'r');
  return pix.outline('o');
}

function windowPane(): Pix {
  const pix = new Pix(30, 18);
  pix.box(0, 0, 30, 18, { fill: 'f', light: 'F', dark: 'o' });
  pix.box(2, 2, 12, 13, { fill: 'g', light: 'G', dark: 'b' });
  pix.box(16, 2, 12, 13, { fill: 'g', light: 'G', dark: 'b' });
  // reflexo diagonal
  pix.px(4, 4, 'G').px(5, 5, 'G').px(18, 4, 'G').px(19, 5, 'G').px(20, 6, 'G');
  pix.hline(0, 16, 30, 'F').hline(0, 17, 30, 'f'); // peitoril
  return pix;
}

/** Quadro de paisagem: moldura de madeira, céu, colinas e sol. */
function landscapeFrame(): Pix {
  const pix = new Pix(16, 14);
  pix.box(0, 0, 16, 13, { fill: 'W', light: 'L', dark: 'D' });
  pix.rect(2, 2, 12, 5, 'G'); // céu
  pix.rect(2, 7, 12, 4, 'g'); // campo
  pix.disc(5, 8, 3, 'h');
  pix.rect(10, 3, 2, 2, 'y'); // sol
  return pix.outline('o');
}

/** Pôster da marca: fundo preto e um "A" vermelho. */
function posterAgzos(): Pix {
  const pix = new Pix(12, 16);
  pix.box(1, 1, 10, 14, { fill: 'd', light: 'k', dark: 'z' });
  pix.paste(['..rr..', '.r..r.', '.r..r.', '.rrrr.', '.r..r.', '.r..r.', '.r..r.'], 3, 4);
  pix.hline(3, 12, 6, 'R');
  return pix.outline('o');
}

/** Quadro de avisos de cortiça com bilhetes coloridos e pins. */
function noticeBoard(): Pix {
  const pix = new Pix(16, 14);
  pix.box(0, 0, 16, 13, { fill: 'W', light: 'L', dark: 'D' });
  pix.rect(2, 2, 12, 9, 'f');
  pix.rect(3, 3, 4, 3, 'e').rect(9, 3, 4, 3, 'y');
  pix.rect(3, 8, 4, 3, 'b').rect(9, 8, 4, 3, 'p');
  pix.px(4, 3, 'r').px(10, 3, 'r').px(4, 8, 'r').px(10, 8, 'r');
  return pix.outline('o');
}

/** TV de parede: tela com uma barra de progresso que anda entre os quadros. */
function wallTv(bar: number): Pix {
  const pix = new Pix(28, 17);
  pix.box(0, 0, 28, 13, { fill: 'k', light: 'h', dark: 'z' });
  pix.rect(2, 2, 24, 9, 's');
  pix.rect(4, 4, bar, 2, 'G');
  pix.hline(4, 8, 14, 'g');
  pix.rect(12, 13, 4, 2, 'k').hline(8, 15, 12, 'K');
  return pix.outline('o');
}

/** Ar-condicionado de parede: corpo branco, grade e LED. */
function airConditioner(): Pix {
  const pix = new Pix(24, 9);
  pix.box(0, 0, 24, 8, { fill: 'b', light: 'c', dark: 'a' });
  // grade de saída de ar embaixo, com aletas e o LED de status
  pix.hline(2, 2, 20, 'a');
  pix.rect(2, 5, 20, 2, 'a').hline(3, 5, 18, 'b');
  pix.px(20, 1, 'G');
  return pix.outline('o');
}

/** Prateleira de parede com livros e uma planta pequena. */
function wallShelf(): Pix {
  const pix = new Pix(24, 14);
  pix.rect(2, 3, 2, 6, 'a').rect(4, 4, 2, 5, 'b').rect(6, 2, 2, 7, 'c');
  pix.disc(18, 4, 3, 'd').disc(17, 3, 2, 'l');
  pix.box(16, 6, 5, 3, { fill: 'q', light: 'Q', dark: 'p' });
  pix.box(0, 9, 24, 3, { fill: 'W', light: 'L', dark: 'D' });
  pix.hline(2, 12, 2, 'D').hline(20, 12, 2, 'D');
  return pix.outline('o');
}

/** Placa de saída: verde com seta branca. */
function exitSign(): Pix {
  const pix = new Pix(14, 9);
  pix.box(0, 0, 14, 8, { fill: 'g', light: 'G', dark: 'd' });
  pix.rect(3, 3, 5, 2, 'w').hline(8, 2, 1, 'w').px(9, 3, 'w').hline(8, 4, 1, 'w');
  return pix.outline('o');
}

/** Calendário de parede: cabeçalho vermelho, anéis e grade de dias. */
function calendar(): Pix {
  const pix = new Pix(12, 15);
  pix.px(3, 0, 'k').px(8, 0, 'k');
  pix.box(0, 1, 12, 13, { fill: 'z', light: 'x', dark: 'q' });
  pix.rect(1, 2, 10, 3, 'r').hline(1, 2, 10, 'R');
  for (const y of [6, 8, 10, 12]) pix.pattern(2, y, 8, 'g.g.g.g.');
  return pix.outline('o');
}

/** Quadro kanban: três colunas com cabeçalho colorido e bilhetes. */
function kanbanBoard(): Pix {
  const pix = new Pix(24, 17);
  pix.box(0, 0, 24, 16, { fill: 'm', light: 'M', dark: 'd' });
  pix.rect(2, 2, 20, 12, 'w');
  pix.vline(8, 2, 12, 'f').vline(15, 2, 12, 'f');
  pix.hline(3, 3, 4, 'r').hline(9, 3, 5, 'b').hline(16, 3, 6, 'g');
  pix.rect(3, 6, 4, 3, 'y').rect(3, 10, 4, 2, 'y');
  pix.rect(9, 6, 5, 3, 'b');
  pix.rect(16, 6, 6, 3, 'g').rect(16, 10, 6, 2, 'y');
  return pix.outline('o');
}

export const WALL_FURNITURE: readonly FurnitureDef[] = [
  {
    id: 'relogio',
    name: 'Relógio de parede',
    category: 'wall',
    footprint: { w: 1, h: 1 },
    wall: true,
    blocks: false,
    sprite: clock().sprite({ o: 'black0', f: 'black2', w: 'white3', k: 'ink', r: 'red2' }),
  },
  {
    id: 'janela',
    name: 'Janela',
    category: 'wall',
    footprint: { w: 2, h: 1 },
    wall: true,
    blocks: false,
    sprite: windowPane().sprite({ o: 'wall0', f: 'white1', F: 'white3', g: 'glass1', G: 'glass3', b: 'glass0' }),
    glow: { color: 'rgba(255, 220, 140, 0.35)', radius: 28, dy: 10 },
  },
  {
    id: 'quadro-paisagem',
    name: 'Quadro de paisagem',
    category: 'wall',
    footprint: { w: 1, h: 1 },
    wall: true,
    blocks: false,
    sprite: landscapeFrame().sprite({ o: 'wood0', D: 'wood1', W: 'wood2', L: 'wood3', G: 'glass2', g: 'leaf2', h: 'leaf1', y: 'light3' }),
  },
  {
    id: 'poster-agzos',
    name: 'Pôster Agzos',
    category: 'wall',
    footprint: { w: 1, h: 1 },
    wall: true,
    blocks: false,
    sprite: posterAgzos().sprite({ o: 'black0', d: 'black1', k: 'black2', z: 'black0', r: 'red2', R: 'red3' }),
  },
  {
    id: 'quadro-avisos',
    name: 'Quadro de avisos',
    category: 'wall',
    footprint: { w: 1, h: 1 },
    wall: true,
    blocks: false,
    sprite: noticeBoard().sprite({ o: 'wood0', D: 'wood1', W: 'wood2', L: 'wood3', f: 'oak2', e: 'paper3', p: 'paper2', y: 'gold3', b: 'fabricBlue3', r: 'red2' }),
  },
  {
    id: 'tv-parede',
    name: 'TV de parede',
    category: 'wall',
    footprint: { w: 2, h: 1 },
    wall: true,
    blocks: false,
    sprite: wallTv(6).sprite({ o: 'black0', k: 'black1', K: 'black2', h: 'black3', z: 'black0', s: 'screen0', g: 'screen2', G: 'screen3' }),
    frames: [wallTv(14).sprite({ o: 'black0', k: 'black1', K: 'black2', h: 'black3', z: 'black0', s: 'screen0', g: 'screen2', G: 'screen3' })],
    frameMs: 900,
  },
  {
    id: 'ar-condicionado',
    name: 'Ar-condicionado',
    category: 'wall',
    footprint: { w: 2, h: 1 },
    wall: true,
    blocks: false,
    sprite: airConditioner().sprite({ o: 'plastic0', a: 'plastic1', b: 'plastic2', c: 'plastic3', G: 'screen3' }),
  },
  {
    id: 'prateleira-parede',
    name: 'Prateleira de parede',
    category: 'wall',
    footprint: { w: 2, h: 1 },
    wall: true,
    blocks: false,
    sprite: wallShelf().sprite({ o: 'wood0', D: 'wood1', W: 'wood2', L: 'wood3', a: 'red2', b: 'fabricBlue2', c: 'fabricGreen2', p: 'pot1', q: 'pot2', Q: 'pot3', d: 'leaf1', l: 'leaf2' }),
  },
  {
    id: 'placa-saida',
    name: 'Placa de saída',
    category: 'wall',
    footprint: { w: 1, h: 1 },
    wall: true,
    blocks: false,
    sprite: exitSign().sprite({ o: 'leaf0', g: 'leaf2', G: 'leaf3', d: 'leaf1', w: 'white3' }),
  },
  {
    id: 'calendario',
    name: 'Calendário',
    category: 'wall',
    footprint: { w: 1, h: 1 },
    wall: true,
    blocks: false,
    sprite: calendar().sprite({ o: 'black0', z: 'paper2', x: 'paper3', q: 'paper1', g: 'paper0', r: 'red2', R: 'red3', k: 'black1' }),
  },
  {
    id: 'quadro-kanban',
    name: 'Quadro kanban',
    category: 'wall',
    footprint: { w: 2, h: 1 },
    wall: true,
    blocks: false,
    sprite: kanbanBoard().sprite({ o: 'black0', m: 'metal2', M: 'metal3', d: 'metal1', w: 'white2', f: 'white1', r: 'red2', b: 'fabricBlue3', g: 'fabricGreen3', y: 'gold3' }),
  },
];
